import { useEffect, useState } from 'react'
import { Col, Flex, Switch, Table, Typography } from 'antd'
import styled from 'styled-components'
import { colours } from '@qflow/theme'

import { useApi } from 'admin/lib/Api'
import { ResponsiveWrapper } from 'admin/lib/Common/StyledElements'
import { Flag } from 'admin/lib/Icons/Flag'
import { Layout } from 'admin/lib/Layouts'
import { ScreenSpin } from 'admin/lib/ScreenSpin'

export function RecordsSummary() {
  const [isLoading, setIsLoading] = useState(true)
  const [showClosedOut, setShowClosedOut] = useState(false)
  const [groupByAccount, setGroupByAccount] = useState(false)
  const [tableKey, setTableKey] = useState(0)
  const [expandedAccounts, setExpandedAccounts] = useState(new Set())
  const [data, setData] = useState([])
  const api = useApi()

  useEffect(() => {
    const handleLoad = async () => {
      return await api.getRecordsSummary()
    }

    setIsLoading(true)
    handleLoad()
      .then(value => {
        setData(value[0])
        setIsLoading(false)
      })
      .catch(() => {
        setData([])
      })
  }, [api])

  const formatTableData = () => {
    let formattedData = []
    const totals = { key: 'total', name: 'TOTAL', account: '', country: '' }
    const IGNORE_ACCOUNT_ARRAY = [
      '26532b6e-ac23-4142-b74d-c48d6cb33d7c', // PROD - Demo UK
      '7bad3c12-4d21-4530-92c7-2d507b071921', // PROD - Demo US
      '269842d3-cfea-47e8-8678-dc27a903f1d7', // PROD - Main Test Account
      '02dedf27-1fc5-44e4-ba5f-1c166cf66898' // PROD - Qflow data admin demo
    ]

    if (data && data.projectSummaries && data.projectSummaries.length > 0) {
      formattedData = data.projectSummaries
        .filter(item => !IGNORE_ACCOUNT_ARRAY.includes(item.account.accountId))
        .filter(item => (showClosedOut ? true : !item.project.isClosedOut))
        .map(item => ({
          key: item.project.projectId,
          name: item.project.name,
          account: item.account.name.trim(),
          country: item.project.country,
          isClosedOut: item.project.isClosedOut,
          New: item.recordCountsByStatus.New
            ? item.recordCountsByStatus.New
            : 0,
          Failed: item.recordCountsByStatus.Failed
            ? item.recordCountsByStatus.Failed
            : 0,
          Escalated: item.recordCountsByStatus.Escalated
            ? item.recordCountsByStatus.Escalated
            : 0,
          Published: item.recordCountsByStatus.Published
            ? item.recordCountsByStatus.Published
            : 0,
          AutoPublished: item.recordCountsByStatus.AutoPublished
            ? item.recordCountsByStatus.AutoPublished
            : 0,
          Removed: item.recordCountsByStatus.Removed
            ? item.recordCountsByStatus.Removed
            : 0,
          Void: item.recordCountsByStatus.Void
            ? item.recordCountsByStatus.Void
            : 0
        }))

      // Get Totals
      totals.New = formattedData.reduce((accum, item) => accum + item.New, 0)
      totals.Failed = formattedData.reduce(
        (accum, item) => accum + item.Failed,
        0
      )
      totals.Escalated = formattedData.reduce(
        (accum, item) => accum + item.Escalated,
        0
      )
      totals.Published = formattedData.reduce(
        (accum, item) => accum + item.Published,
        0
      )
      totals.AutoPublished = formattedData.reduce(
        (accum, item) => accum + item.AutoPublished,
        0
      )
      totals.Removed = formattedData.reduce(
        (accum, item) => accum + item.Removed,
        0
      )
      totals.Void = formattedData.reduce((accum, item) => accum + item.Void, 0)
    }

    if (groupByAccount && formattedData.length > 0) {
      const sorted = [...formattedData].sort((a, b) => {
        const aAcct = (a.account || '').trim().toLowerCase()
        const bAcct = (b.account || '').trim().toLowerCase()
        return aAcct.localeCompare(bAcct)
      })

      // First pass: calculate totals per account
      const accountTotals = {}
      for (const row of sorted) {
        const acctKey = (row.account || '').trim().toLowerCase()
        if (!accountTotals[acctKey]) {
          accountTotals[acctKey] = {
            account: row.account,
            New: 0,
            Failed: 0,
            Escalated: 0,
            Published: 0,
            AutoPublished: 0,
            Removed: 0,
            Void: 0
          }
        }
        accountTotals[acctKey].New += row.New
        accountTotals[acctKey].Failed += row.Failed
        accountTotals[acctKey].Escalated += row.Escalated
        accountTotals[acctKey].Published += row.Published
        accountTotals[acctKey].AutoPublished += row.AutoPublished
        accountTotals[acctKey].Removed += row.Removed
        accountTotals[acctKey].Void += row.Void
      }

      // Second pass: build grouped array with totals
      const grouped = []
      let currentAccount = null
      let groupCount = 0
      for (const row of sorted) {
        const rowAccount = (row.account || '').trim().toLowerCase()
        if (rowAccount !== currentAccount) {
          currentAccount = rowAccount
          groupCount++
          const totalsForAccount = accountTotals[rowAccount]
          grouped.push({
            key: `group-${groupCount}`,
            isAccountGroup: true,
            account: row.account,
            accountKey: rowAccount,
            name: '',
            country: '',
            isClosedOut: false,
            New: totalsForAccount.New,
            Failed: totalsForAccount.Failed,
            Escalated: totalsForAccount.Escalated,
            Published: totalsForAccount.Published,
            AutoPublished: totalsForAccount.AutoPublished,
            Removed: totalsForAccount.Removed,
            Void: totalsForAccount.Void
          })
        }
        if (expandedAccounts.has(rowAccount)) {
          grouped.push(row)
        }
      }
      return [totals, ...grouped]
    }

    return [totals, ...formattedData]
  }

  const groupNumericOnCell = record =>
    record.isAccountGroup
      ? {
          style: {
            color: colours.WHITE,
            backgroundColor: colours.CHARCOAL_GREY
          }
        }
      : {}

  const renderNumericCell = (value, record) =>
    record.isAccountGroup ? <strong>{value}</strong> : value

  const createNumericSorter = field => (a, b) => {
    if (a.isAccountGroup || b.isAccountGroup) return 0
    if (groupByAccount) return 0
    if (b.name === 'TOTAL') return 1
    return a[field] - b[field]
  }

  const stringSorter = (a, b) => {
    if (a.isAccountGroup || b.isAccountGroup) return 0
    if (groupByAccount) return 0
    if (b.name === 'TOTAL') return 1
    return a.name.localeCompare(b.name)
  }

  const columns = [
    {
      title: '',
      dataIndex: 'country',
      width: '36px',
      onCell: record => (record.isAccountGroup ? { colSpan: 3 } : {}),
      render: (country, record) => {
        if (record.isAccountGroup) {
          const isCollapsed = !expandedAccounts.has(record.accountKey)
          const toggleCollapse = () => {
            setExpandedAccounts(prev => {
              const next = new Set(prev)
              if (next.has(record.accountKey)) {
                next.delete(record.accountKey)
              } else {
                next.add(record.accountKey)
              }
              return next
            })
          }
          return (
            <AccountGroupHeader onClick={toggleCollapse}>
              <span
                style={{
                  marginRight: '8px',
                  fontSize: '10px',
                  display: 'inline-block',
                  transform: isCollapsed ? 'rotate(-90deg)' : 'none'
                }}
              >
                ▼
              </span>
              {record.account}
            </AccountGroupHeader>
          )
        }
        return (
          <Flag
            country={country}
            style={{ width: '28px', margin: '0 -8px -2px 0' }}
          />
        )
      }
    },
    {
      title: groupByAccount ? 'Account/Project' : 'Project',
      dataIndex: 'name',
      sorter: stringSorter,
      sortDirections: groupByAccount ? [] : ['ascend'],
      onCell: record => (record.isAccountGroup ? { colSpan: 0 } : {}),
      render: (value, record) => {
        if (record.isAccountGroup) return null
        return (
          <>
            <Col>
              <Typography.Text strong={true}>{value}</Typography.Text>
            </Col>
            <Col>
              <Typography.Text italic={true}>{record.account}</Typography.Text>
            </Col>
          </>
        )
      }
    },
    {
      title: '',
      dataIndex: 'isClosedOut',
      width: '54px',
      onCell: record =>
        record.isAccountGroup
          ? { colSpan: 0 }
          : { style: { padding: '0', margin: '0', textAlign: 'center' } },
      render: value => {
        if (value) {
          return <ClosedOutBadge strong={true}>Closed Out</ClosedOutBadge>
        }
        return null
      }
    },
    {
      title: 'New',
      dataIndex: 'New',
      width: '100px',
      align: 'center',
      sorter: createNumericSorter('New'),
      sortDirections: groupByAccount ? [] : ['descend'],
      onCell: groupNumericOnCell,
      render: renderNumericCell
    },
    {
      title: 'Failed',
      dataIndex: 'Failed',
      width: '100px',
      align: 'center',
      sorter: createNumericSorter('Failed'),
      sortDirections: groupByAccount ? [] : ['descend'],
      defaultSortOrder: groupByAccount ? undefined : 'descend',
      onCell: groupNumericOnCell,
      render: renderNumericCell
    },
    {
      title: 'Escalated',
      dataIndex: 'Escalated',
      width: '115px',
      align: 'center',
      sorter: createNumericSorter('Escalated'),
      sortDirections: groupByAccount ? [] : ['descend'],
      onCell: groupNumericOnCell,
      render: renderNumericCell
    },
    {
      title: 'Published',
      dataIndex: 'Published',
      width: '115px',
      align: 'center',
      sorter: createNumericSorter('Published'),
      sortDirections: groupByAccount ? [] : ['descend'],
      onCell: groupNumericOnCell,
      render: renderNumericCell
    },
    {
      title: 'AutoPublished',
      dataIndex: 'AutoPublished',
      width: '130px',
      align: 'center',
      sorter: createNumericSorter('AutoPublished'),
      sortDirections: groupByAccount ? [] : ['descend'],
      onCell: groupNumericOnCell,
      render: renderNumericCell
    },
    {
      title: 'Removed',
      dataIndex: 'Removed',
      width: '115px',
      align: 'center',
      sorter: createNumericSorter('Removed'),
      sortDirections: groupByAccount ? [] : ['descend'],
      onCell: groupNumericOnCell,
      render: renderNumericCell
    },
    {
      title: 'Void',
      dataIndex: 'Void',
      width: '100px',
      align: 'center',
      sorter: createNumericSorter('Void'),
      sortDirections: groupByAccount ? [] : ['descend'],
      onCell: groupNumericOnCell,
      render: renderNumericCell
    }
  ]

  return (
    <Layout>
      <ResponsiveWrapper style={{ margin: '1.5rem auto' }}>
        <div>
          <h1>Records Summary</h1>
          <Flex align="center" gap={'1rem'}>
            <Typography.Title level={5} style={{ margin: '0 0 0 0.25rem' }}>
              Show Closed Out
            </Typography.Title>
            <Switch
              checked={showClosedOut}
              checkedChildren="Yes"
              unCheckedChildren="No"
              onChange={checked => setShowClosedOut(checked)}
            />
            <Typography.Title level={5} style={{ margin: '0 0 0 0.25rem' }}>
              Group by Account
            </Typography.Title>
            <Switch
              checked={groupByAccount}
              checkedChildren="Yes"
              unCheckedChildren="No"
              onChange={checked => {
                setGroupByAccount(checked)
                if (checked) {
                  setTableKey(k => k + 1)
                  setExpandedAccounts(new Set())
                }
              }}
            />
          </Flex>
        </div>
        {isLoading && <ScreenSpin id="userLoadingSpinner" />}
        {!isLoading && (
          <StyledTable
            key={tableKey}
            size="middle"
            columns={columns}
            dataSource={formatTableData()}
            pagination={false}
            scroll={{ y: 'calc(100vh - 248px)' }}
            sticky
            rowKey="key"
            onRow={record => {
              if (record.isAccountGroup) {
                return {
                  className: 'account-group-row',
                  style: {
                    backgroundColor: colours.CHARCOAL_GREY,
                    color: colours.WHITE
                  }
                }
              }
              return {}
            }}
          />
        )}
      </ResponsiveWrapper>
    </Layout>
  )
}

export const AccountGroupHeader = styled.div`
  display: flex;
  align-items: center;
  font-weight: 600;
  color: ${colours.WHITE};
  padding: 4px 0;
  cursor: pointer;
  user-select: none;
`

const StyledTable = styled(Table)`
  .ant-table-tbody > tr.account-group-row:hover > td {
    background: ${colours.CHARCOAL_GREY} !important;
  }
`

export const ClosedOutBadge = styled(Typography.Text)`
  display: inline-block;
  min-width: 32px;
  background-color: ${colours.WHITE};
  color: ${colours.RED} !important;
  border: 1px solid ${colours.RED};
  padding: 1px 4px;
  border-radius: 5px;
  margin: 0 0 1px;
`
